// Watches an owner-paid Write Onchain workflow until it settles, so its toast can resolve in
// place. The media tile does not depend on this: the workflow's final answer update re-renders
// the row whether the write landed or failed.

export type WriteOnchainProgressEntry = {
    message?: string
    completed?: boolean
    error?: boolean
}

export type WriteOnchainWorkflowStatus = {
    status?: string
    result?: WriteOnchainProgressEntry[] | null
}

export type WriteOnchainOutcome =
    | { settled: true; ok: boolean; message: string }
    | { settled: false; message: string }

export const WRITE_ONCHAIN_WORKFLOW_CLASS = 'AppTemporal::Workflows::AttachmentWorkflow'

const defaultSleep = (ms: number) => new Promise<void>(resolve => setTimeout(resolve, ms))

// A running workflow reports its progress log, whose last entry is marked `completed` when it
// ends; a finished one is reported by status. Either can settle the outcome.
export function settledWriteOnchainOutcome(
    response: WriteOnchainWorkflowStatus | null | undefined,
): WriteOnchainOutcome | null {
    const progress = Array.isArray(response?.result) ? response.result : []
    const last = progress[progress.length - 1]
    if (last?.completed === true) {
        return { settled: true, ok: last.error !== true, message: last.message || '' }
    }
    if (response?.status === 'completed' || response?.status === 'failed') {
        return { settled: true, ok: response.status === 'completed', message: last?.message || '' }
    }
    return null
}

export async function waitForWriteOnchainOutcome({
    fetchStatus,
    sleep = defaultSleep,
    now = () => Date.now(),
    firstDelayMs = 2000,
    maxDelayMs = 10000,
    // A 50MB write's activity may run for up to 20 minutes; stop watching a little after that.
    maxWaitMs = 25 * 60 * 1000,
    maxConsecutiveErrors = 5,
}: {
    fetchStatus: () => Promise<WriteOnchainWorkflowStatus>
    sleep?: (ms: number) => Promise<void>
    now?: () => number
    firstDelayMs?: number
    maxDelayMs?: number
    maxWaitMs?: number
    maxConsecutiveErrors?: number
}): Promise<WriteOnchainOutcome> {
    const deadline = now() + maxWaitMs
    let delayMs = firstDelayMs
    let consecutiveErrors = 0

    while (now() < deadline) {
        await sleep(delayMs)
        delayMs = Math.min(maxDelayMs, Math.round(delayMs * 1.5))

        try {
            const outcome = settledWriteOnchainOutcome(await fetchStatus())
            if (outcome) return outcome
            consecutiveErrors = 0
        } catch {
            consecutiveErrors += 1
            if (consecutiveErrors >= maxConsecutiveErrors) break
        }
    }

    return { settled: false, message: 'Still writing onchain' }
}
